import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import Barcode from "react-barcode";
import { Loader2, ScanLine, Warehouse, Wrench, Archive, ArrowRightLeft, AlertTriangle, Plus, Trash2, Printer, X } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import { useConfirm } from "@/components/ConfirmDialog";
import api, { apiError } from "@/lib/api";

const STATUS_STYLES = {
  in_use: "text-[#c00000] bg-[#c00000]/10 border-[#c00000]/30",
  lager: "text-[#c00000] bg-[#c00000]/10 border-[#c00000]/30",
  defekt: "text-red-700 bg-red-500/10 border-red-500/30",
  ausgemustert: "text-gray-600 bg-gray-50 border-gray-200",
};
const STATUS_LABEL = { in_use: "Im Einsatz", lager: "Lager", defekt: "Defekt", ausgemustert: "Ausgemustert" };

function ScannerLabel({ scanner, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="scanner-label-modal">
      <div className="bg-white text-black rounded-lg p-5 w-[320px]" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-semibold text-gray-700">Scanner-Etikett</span>
          <button onClick={onClose} data-testid="close-label"><X className="w-4 h-4 text-gray-600" /></button>
        </div>
        <div className="flex flex-col items-center gap-2 border border-gray-300 rounded p-3" id="scanner-label-print">
          <QRCodeSVG value={scanner.asset_tag} size={110} />
          <Barcode value={scanner.asset_tag} height={38} width={1.6} displayValue={false} margin={0} />
          <div className="text-lg font-bold tracking-wide">{scanner.asset_tag}</div>
          <div className="text-xs text-gray-600">{scanner.scanner_serial} · {scanner.scanner_type || "—"}</div>
        </div>
        <button onClick={() => window.print()} data-testid="print-label" className="mt-3 w-full flex items-center justify-center gap-2 bg-gray-900 text-white rounded-lg py-2 text-sm">
          <Printer className="w-4 h-4" /> Drucken
        </button>
      </div>
    </div>
  );
}

export default function Scanners() {
  const { lang } = useLang();
  const de = lang === "de";
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [label, setLabel] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ scanner_serial: "", scanner_type: "Regula", condition: "gebraucht" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/scanners"); setItems(r.data || []); }
    catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (ident, fn, okMsg) => {
    setBusy(ident);
    try { await fn(); toast.success(okMsg); await load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const toLager = (s) => confirm({ title: "Ins Lager?", message: `Scanner ${s.asset_tag} (${s.scanner_serial}) einlagern?`, confirmLabel: "Einlagern" })
    .then((ok) => ok && act(s.asset_tag, () => api.post(`/scanners/${s.asset_tag}/lager`, { condition: "gebraucht" }), "Eingelagert"));
  const toDefekt = (s) => confirm({ title: "Als defekt markieren?", message: `Scanner ${s.asset_tag} als defekt markieren?`, confirmLabel: "Defekt", danger: true })
    .then((ok) => ok && act(s.asset_tag, () => api.post(`/scanners/${s.asset_tag}/condition`, { condition: "defekt" }), "Als defekt markiert"));
  const toRetire = (s) => confirm({ title: "Ausmustern?", message: `Scanner ${s.asset_tag} ausmustern?`, confirmLabel: "Ausmustern", danger: true })
    .then((ok) => ok && act(s.asset_tag, () => api.post(`/scanners/${s.asset_tag}/condition`, { condition: "ausgemustert" }), "Ausgemustert"));
  const del = (s) => confirm({ title: "Scanner löschen?", message: `Scanner ${s.asset_tag} endgültig löschen?`, confirmLabel: "Löschen", danger: true })
    .then((ok) => ok && act(s.asset_tag, () => api.delete(`/scanners/${s.asset_tag}`), "Gelöscht"));
  const assign = (s) => {
    const code = window.prompt(de ? "Ziel-Tablet (Geräte-Code):" : "Target tablet (device code):", s.current_device_code || "");
    if (code && code.trim()) act(s.asset_tag, () => api.post(`/scanners/${s.asset_tag}/assign`, { device_code: code.trim() }), "Zugewiesen");
  };
  const resolveMove = (s) => confirm({ title: "Konflikt: umziehen?", message: `Scanner ${s.scanner_serial} auf das meldende Tablet ${s.conflict?.claimed_by_code} umziehen (weg von ${s.conflict?.current_code || "—"})?`, confirmLabel: "Umziehen" })
    .then((ok) => ok && act(s.asset_tag, () => api.post(`/scanners/${s.asset_tag}/resolve-conflict`, { device_code: s.conflict?.claimed_by_code }), "Umgezogen"));
  const resolveDismiss = (s) => act(s.asset_tag, () => api.post(`/scanners/${s.asset_tag}/resolve-conflict`, { device_code: "" }), "Konflikt verworfen");

  const createScanner = async () => {
    if (!form.scanner_serial.trim()) { toast.error("Seriennummer erforderlich"); return; }
    try { await api.post("/scanners", form); toast.success("Scanner angelegt"); setShowCreate(false); setForm({ scanner_serial: "", scanner_type: "Regula", condition: "gebraucht" }); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const conflicts = items.filter((s) => s.conflict);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="w-full px-4 sm:px-6 py-8 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ScanLine className="w-6 h-6 text-[#c00000]" /> {de ? "Scanner-Register" : "Scanners"}</h1>
            <p className="text-sm text-gray-600 mt-1">{items.length} {de ? "Scanner · eindeutige Seriennummern, permanente SC-AssetIDs" : "scanners"}</p>
          </div>
          <button data-testid="scanner-create-btn" onClick={() => setShowCreate((v) => !v)} className="flex items-center gap-2 bg-[#c00000] hover:bg-[#c00000] text-white font-semibold rounded-lg px-4 py-2.5 text-sm">
            <Plus className="w-4 h-4" /> {de ? "Scanner (Lager) anlegen" : "Add scanner"}
          </button>
        </div>

        {showCreate && (
          <div className="card-surface rounded-xl p-4 flex flex-wrap items-end gap-3" data-testid="scanner-create-form">
            <div><label className="text-xs text-gray-600 block mb-1">Seriennummer (Label)</label><input data-testid="create-serial" value={form.scanner_serial} onChange={(e) => setForm({ ...form, scanner_serial: e.target.value })} className="bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 font-mono" placeholder="7EA1250BA4825" /></div>
            <div><label className="text-xs text-gray-600 block mb-1">Typ</label><select value={form.scanner_type} onChange={(e) => setForm({ ...form, scanner_type: e.target.value })} className="bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"><option>Regula</option><option>DESKO PentaScanner</option></select></div>
            <button data-testid="create-submit" onClick={createScanner} className="bg-[#c00000] text-white font-semibold rounded-lg px-4 py-2 text-sm">Anlegen</button>
          </div>
        )}

        {conflicts.length > 0 && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 space-y-2" data-testid="scanner-conflicts">
            <div className="flex items-center gap-2 text-red-700 font-semibold"><AlertTriangle className="w-4 h-4" /> {conflicts.length} {de ? "Scanner-Konflikt(e) – bitte entscheiden" : "conflict(s)"}</div>
            {conflicts.map((s) => (
              <div key={s.asset_tag} className="flex flex-wrap items-center gap-2 text-sm bg-black/20 rounded p-2" data-testid={`conflict-${s.asset_tag}`}>
                <span className="font-mono text-red-200">{s.scanner_serial}</span>
                <span className="text-gray-600">wird von <b className="text-gray-900">{s.conflict?.claimed_by_code}</b> gemeldet, ist aber <b className="text-gray-900">{s.conflict?.current_code || "—"}</b> zugewiesen.</span>
                <button data-testid={`conflict-move-${s.asset_tag}`} onClick={() => resolveMove(s)} className="ml-auto bg-amber-500/20 border border-amber-500/40 text-amber-200 rounded px-2 py-1 text-xs">Auf {s.conflict?.claimed_by_code} umziehen</button>
                <button data-testid={`conflict-dismiss-${s.asset_tag}`} onClick={() => resolveDismiss(s)} className="bg-gray-50 border border-gray-200 text-gray-700 rounded px-2 py-1 text-xs">Verwerfen</button>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-600"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="card-surface rounded-xl">
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left" data-testid="scanner-table">
              <thead className="text-xs uppercase text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="px-2 py-3 font-medium">AssetID</th>
                  <th className="px-2 py-3 font-medium">Seriennr.</th>
                  <th className="px-2 py-3 font-medium">Typ</th>
                  <th className="px-2 py-3 font-medium">Status</th>
                  <th className="px-2 py-3 font-medium">Verbunden</th>
                  <th className="px-2 py-3 font-medium">Tablet</th>
                  <th className="px-2 py-3 font-medium">Standort</th>
                  <th className="px-2 py-3 font-medium text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {items.map((s) => (
                  <tr key={s.asset_tag} className={`border-b border-gray-100 hover:bg-gray-50 ${s.conflict ? "bg-red-500/5" : ""}`} data-testid={`scanner-row-${s.asset_tag}`}>
                    <td className="px-2 py-2.5 font-mono font-bold text-gray-900">{s.asset_tag}</td>
                    <td className="px-2 py-2.5 font-mono text-gray-700">{s.scanner_serial}</td>
                    <td className="px-2 py-2.5 text-gray-700">{s.scanner_type || "—"}</td>
                    <td className="px-2 py-2.5"><span className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLES[s.status] || ""}`}>{STATUS_LABEL[s.status] || s.status}</span></td>
                    <td className="px-2 py-2.5">
                      {s.connected === "ja" ? (
                        <span className="inline-block w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.7)]" data-testid={`led-${s.asset_tag}`} title="Scanner verbunden" />
                      ) : s.connected === "nein" ? (
                        <span className="inline-block w-3 h-3 rounded-full bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.7)]" data-testid={`led-${s.asset_tag}`} title="Scanner getrennt" />
                      ) : (
                        <span className="inline-block w-3 h-3 rounded-full bg-gray-600" data-testid={`led-${s.asset_tag}`} title="Status unbekannt" />
                      )}
                    </td>
                    <td className="px-2 py-2.5 font-mono text-amber-700">{s.current_device_code || "—"}</td>
                    <td className="px-2 py-2.5 text-gray-600">{s.location_name || s.location_id || "—"}</td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button title="Etikett" data-testid={`label-${s.asset_tag}`} onClick={() => setLabel(s)} className="p-1.5 rounded hover:bg-gray-100 text-gray-700"><Printer className="w-4 h-4" /></button>
                        <button title="Zuweisen" data-testid={`assign-${s.asset_tag}`} onClick={() => assign(s)} className="p-1.5 rounded hover:bg-gray-100 text-[#c00000]"><ArrowRightLeft className="w-4 h-4" /></button>
                        <button title="Lager" data-testid={`lager-${s.asset_tag}`} onClick={() => toLager(s)} className="p-1.5 rounded hover:bg-gray-100 text-[#c00000]"><Warehouse className="w-4 h-4" /></button>
                        <button title="Defekt" data-testid={`defekt-${s.asset_tag}`} onClick={() => toDefekt(s)} className="p-1.5 rounded hover:bg-gray-100 text-red-700"><Wrench className="w-4 h-4" /></button>
                        <button title="Ausmustern" data-testid={`retire-${s.asset_tag}`} onClick={() => toRetire(s)} className="p-1.5 rounded hover:bg-gray-100 text-gray-600"><Archive className="w-4 h-4" /></button>
                        <button title="Löschen" data-testid={`del-${s.asset_tag}`} onClick={() => del(s)} className="p-1.5 rounded hover:bg-gray-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-600">Noch keine Scanner erfasst</td></tr>}
              </tbody>
            </table>
            </div>

            <div className="md:hidden divide-y divide-gray-100" data-testid="scanner-cards-mobile">
              {items.length === 0 && <div className="px-4 py-10 text-center text-gray-600 text-sm">Noch keine Scanner erfasst</div>}
              {items.map((s) => (
                <div key={s.asset_tag} data-testid={`scanner-card-${s.asset_tag}`} className={`p-3.5 flex flex-col gap-2.5 ${s.conflict ? "bg-red-500/5" : ""}`}>
                  <div className="flex items-center gap-2.5">
                    {s.connected === "ja" ? (
                      <span className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.7)] shrink-0" title="verbunden" />
                    ) : s.connected === "nein" ? (
                      <span className="w-3 h-3 rounded-full bg-red-500 shadow-[0_0_8px_2px_rgba(239,68,68,0.7)] shrink-0" title="getrennt" />
                    ) : (
                      <span className="w-3 h-3 rounded-full bg-gray-600 shrink-0" title="unbekannt" />
                    )}
                    <span className="font-mono text-sm font-bold text-gray-900">{s.asset_tag}</span>
                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border shrink-0 ${STATUS_STYLES[s.status] || ""}`}>{STATUS_LABEL[s.status] || s.status}</span>
                    <span className="ml-auto text-xs text-gray-600 shrink-0">{s.scanner_type || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap pl-[26px] text-xs">
                    <span className="font-mono text-gray-700">{s.scanner_serial}</span>
                    {s.current_device_code && <span className="font-mono text-amber-700">{s.current_device_code}</span>}
                    {(s.location_name || s.location_id) && <span className="text-gray-600 truncate max-w-[55%]">{s.location_name || s.location_id}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap pl-[26px]">
                    <button title="Etikett" data-testid={`m-label-${s.asset_tag}`} onClick={() => setLabel(s)} className="p-2 rounded bg-gray-50 border border-gray-200 text-gray-700"><Printer className="w-4 h-4" /></button>
                    <button title="Zuweisen" data-testid={`m-assign-${s.asset_tag}`} onClick={() => assign(s)} className="p-2 rounded bg-gray-50 border border-gray-200 text-[#c00000]"><ArrowRightLeft className="w-4 h-4" /></button>
                    <button title="Lager" data-testid={`m-lager-${s.asset_tag}`} onClick={() => toLager(s)} className="p-2 rounded bg-gray-50 border border-gray-200 text-[#c00000]"><Warehouse className="w-4 h-4" /></button>
                    <button title="Defekt" data-testid={`m-defekt-${s.asset_tag}`} onClick={() => toDefekt(s)} className="p-2 rounded bg-gray-50 border border-gray-200 text-red-700"><Wrench className="w-4 h-4" /></button>
                    <button title="Ausmustern" data-testid={`m-retire-${s.asset_tag}`} onClick={() => toRetire(s)} className="p-2 rounded bg-gray-50 border border-gray-200 text-gray-600"><Archive className="w-4 h-4" /></button>
                    <button title="Löschen" data-testid={`m-del-${s.asset_tag}`} onClick={() => del(s)} className="p-2 rounded bg-gray-50 border border-gray-200 text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      {label && <ScannerLabel scanner={label} onClose={() => setLabel(null)} />}
    </div>
  );
}
